/**
 * useNetobsLatencyAggregate — subscribes to the server's
 * `netobs:latency_aggregate` broadcast (every ~5s) and returns the
 * latest cross-player snapshot. Powers NetobsLatencyHeatmapPanel (NO-7).
 *
 * Backed by useColyseusMessages so we get the bind/rebind-on-reconnect
 * semantics for free.
 */

import { useEffect, useState } from 'react';
import { useColyseusMessages } from '../hooks/useColyseusMessages';
import { isPopoutMode } from '../../systems/netobs/popout/popoutMode';
import { popoutShim } from '../../systems/netobs/popout/popoutShimState';

export interface ClientLatencySnap {
  sid: string;
  name?: string;
  phase1Ms: number;
  phase2Ms: number;
  rttMs: number;
  clockReady: boolean;
  updatedAt: number;
}

export interface NetobsLatencyAggregate {
  snaps: ClientLatencySnap[];
  /** Server wall-clock at broadcast time. */
  timestamp: number;
  /** Local wall-clock when this client received the broadcast. */
  receivedAt: number;
}

export function useNetobsLatencyAggregate(): NetobsLatencyAggregate | null {
  const [snap, setSnap] = useState<NetobsLatencyAggregate | null>(null);
  const popout = isPopoutMode();

  useColyseusMessages({
    'netobs:latency_aggregate': (msg: any) => {
      if (popout) return;
      if (!msg || !Array.isArray(msg.snaps)) return;
      setSnap({
        snaps: msg.snaps as ClientLatencySnap[],
        timestamp: Number(msg.timestamp) || Date.now(),
        receivedAt: Date.now(),
      });
    },
  });

  // Popout mode: poll the BroadcastChannel-fed shim.
  useEffect(() => {
    if (!popout) return;
    const tick = () => {
      const l = popoutShim.getLatency();
      if (l) setSnap(l);
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [popout]);

  return snap;
}
